import React, { ChangeEvent, useCallback, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import clsx from 'clsx'
import _ from 'lodash'

import $scope from 'src/libs/scope'
import Storage from 'src/libs/storage'
import BaseRoundedInput from 'src/components/inputs/BaseRoundedInput'
import BaseSwitch from 'src/components/inputs/BaseSwitch'
import BaseTooltip from 'src/pages/Home/components/BaseTooltip'
import { PairInfo } from 'src/pages/Home/components/SettingDetail'

import { AppDispatch } from 'src/store'
import {
  selectCustomStratEditors,
  selectGunbotMarketType,
  selectGunbotPairOverride,
  setPairOverride,
} from 'src/store/bots'
import { CustomStratEditors, StratSetting } from 'src/store/bots/bots.api'

const easyStratEditor = $scope.easyStratEditor as unknown as CustomStratEditors
const titles = $scope.title as { [key: string]: string }

type Props = { rowData: PairInfo }

function TableDetailPanelOfPair({ rowData }: Props) {
  const { url, exchange, pair, strategy } = rowData
  const dispatch = useDispatch<AppDispatch>()
  const pairOverride = useSelector(selectGunbotPairOverride(url, exchange, pair), _.isEqual)
  const marketType = useSelector(selectGunbotMarketType(url, exchange), _.isEqual)
  const customStratEditors = useSelector(selectCustomStratEditors(url), _.isEqual)

  const allTemplates: CustomStratEditors = useMemo(() => {
    const temp: CustomStratEditors = {}
    if (customStratEditors) {
      Object.keys(customStratEditors).forEach((key) => {
        const lower = key.toLowerCase()
        temp[lower] = {
          ...customStratEditors[key],
          info: {
            ...(customStratEditors[key]?.info || {}),
            license: [
              'ultimate', 'promoultimate', 'br', 'mm', 'promostandard', 'standard',
              'pro', 'promopro', 'standardtv', 'protv', 'monthlyStandard', 'yearlyStandard',
              'monthlyPro', 'yearlyPro', 'monthlyUltimate', 'yearlyUltimate',
            ],
          },
        }
      })
    }
    return { ...Object(easyStratEditor), ...temp } as unknown as CustomStratEditors
  }, [customStratEditors])

  const templateName = useMemo(
    () => getRelevantEditorReference(strategy, marketType, allTemplates),
    [strategy, marketType, allTemplates]
  )
  const template = allTemplates?.[templateName]

  const categoryOrder = [
    'Buy settings',
    'Sell settings',
    'Balance settings',
    'Indicators',
    'Safety settings',
    'Misc settings',
    'Custom overrides',
  ]

  const sectionNames = useMemo(() => {
    if (!template) return []
    return Object.keys(template).filter((k) =>
      categoryOrder.some((cat) => k.toLowerCase().includes(cat.toLowerCase()))
    )
  }, [template])

  const [selectedTab, setSelectedTab] = useState<string>(
    Storage.get(`${url}/${exchange}/${pair}/selectedTab`) ||
      sectionNames[0] ||
      'Buy settings'
  )

  const handleSelectTab = useCallback(
    (name: string) => {
      setSelectedTab(name)
      Storage.set(`${url}/${exchange}/${pair}/selectedTab`, name)
    },
    [url, exchange, pair]
  )

  const handleSwitch = useCallback(
    (name: string) => (e: ChangeEvent<HTMLInputElement>) => {
      dispatch(setPairOverride({ url, exchange, pair, name, value: e.target.checked }))
    },
    [url, exchange, pair]
  )

  const handleInput = useCallback(
    (name: string) => (e: ChangeEvent<HTMLInputElement>) => {
      dispatch(setPairOverride({ url, exchange, pair, name, value: e.target.value }))
    },
    [url, exchange, pair]
  )

  const flattenParameters = (section: any): Record<string, StratSetting> => {
    const result: Record<string, StratSetting> = {}
    const walk = (obj: any) => {
      if (!obj) return
      if (obj.parameters) walk(obj.parameters)
      else if (typeof obj === 'object') {
        Object.keys(obj).forEach((key) => {
          const val = obj[key]
          if (val && typeof val === 'object' && !val.inputType && !val.defaultValue) walk(val)
          else result[key] = val
        })
      }
    }
    walk(section)
    return result
  }

  const knownKeys = useMemo(() => {
    const keys = new Set<string>()
    sectionNames.forEach((section) => {
      const s = template?.[section]
      if (!s) return
      const params = flattenParameters(s)
      Object.keys(params).forEach((k) => keys.add(k))
    })
    return keys
  }, [template, sectionNames])

  const customOverrides: Record<string, StratSetting> = useMemo(() => {
    const out: Record<string, StratSetting> = {}
    if (pairOverride) {
      Object.keys(pairOverride).forEach((k) => {
        if (!knownKeys.has(k)) {
          out[k] = { inputType: 'string', defaultValue: pairOverride[k] as any } as StratSetting
        }
      })
    }
    return out
  }, [pairOverride, knownKeys])

  const [customKey, setCustomKey] = useState('')
  const [customValue, setCustomValue] = useState('')
  const handleAddCustom = useCallback(() => {
    const key = customKey.trim()
    if (!key) return
    let value: string | boolean = customValue
    const lower = customValue.toLowerCase()
    if (lower === 'true') value = true
    else if (lower === 'false') value = false
    dispatch(setPairOverride({ url, exchange, pair, name: key, value }))
    setCustomKey('')
    setCustomValue('')
  }, [customKey, customValue, url, exchange, pair, dispatch])

  const displayedParams = useMemo(() => {
    if (selectedTab === 'Custom overrides') return customOverrides
    const section = template?.[selectedTab]
    if (!section) return {}
    return flattenParameters(section)
  }, [template, selectedTab, customOverrides])

  return (
    <div className="flex flex-col h-[80vh] w-full min-w-0 overflow-hidden">
      {/* Add custom setting */}
      <div className="rounded-lg border border-white/10 bg-[#1f2127] p-4 mb-4 w-full">
        <div className="mb-3 text-sm font-semibold text-white/80">Add custom setting</div>
        <div className="flex flex-wrap gap-3 w-full">
          <BaseRoundedInput
            id="customKey"
            placeholder="Setting name"
            className="flex-1 min-w-[150px]"
            value={customKey}
            onChange={(e) => setCustomKey(e.target.value)}
          />
          <BaseRoundedInput
            id="customValue"
            placeholder="Setting value (true / false for toggle)"
            className="flex-1 min-w-[150px]"
            value={customValue}
            onChange={(e) => setCustomValue(e.target.value)}
          />
          <button
            onClick={handleAddCustom}
            className="rounded-md bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700 active:scale-[0.98]"
          >
            Add
          </button>
        </div>
      </div>

      {/* Tabs header */}
      <div className="sticky top-0 z-20 flex gap-4 border-b border-white/10 bg-[#1b1d22]/95 backdrop-blur-sm px-2 py-2 overflow-x-auto shadow-sm">
        {categoryOrder
          .filter(
            (cat) =>
              sectionNames.some((n) =>
                n.toLowerCase().includes(cat.toLowerCase())
              ) || (cat === 'Custom overrides' && Object.keys(customOverrides).length)
          )
          .map((cat) => (
            <div
              key={cat}
              onClick={() => handleSelectTab(cat)}
              className={clsx(
                'cursor-pointer select-none px-3 py-1 text-sm font-medium transition-all duration-200 border-b-2 whitespace-nowrap',
                selectedTab === cat
                  ? 'border-green-500 text-white'
                  : 'border-transparent text-[#a0a0a0] hover:text-white hover:border-white/20'
              )}
            >
              {cat}
            </div>
          ))}
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden p-3 w-full min-w-0">
        {!displayedParams || !Object.keys(displayedParams).length ? (
          <div className="text-center text-sm text-[#848e9c] mt-6">
            No editable settings in this section.
          </div>
        ) : (
          <div
            className="
              grid
              auto-rows-min
              gap-4
              rounded-lg
              border border-white/5
              p-4
              w-full
              min-w-0
              [grid-template-columns:repeat(auto-fill,minmax(260px,1fr))]
            "
          >
            {Object.keys(displayedParams).map((settingName) => {
              const option = displayedParams[settingName]
              if (!option) return null
              const id = `${url}/${exchange}/${pair}/${settingName}`
              const tooltip = option.tooltip || ($scope.tooltips as any)?.[settingName] || ''
              const val = pairOverride?.[settingName]
              const isBool =
                (option.inputType === 'boolean' || typeof val === 'boolean') &&
                val !== 'true' &&
                val !== 'false'

              return (
                <div
                  key={settingName}
                  className="group relative flex flex-col gap-2 rounded-md border border-white/10 bg-[#2b2e35] p-3 hover:border-green-500/30 transition-all duration-150 w-full min-w-0"
                >
                  <label htmlFor={id} className="flex items-center gap-1 text-sm truncate">
                    {titles[settingName] || settingName}
                    <BaseTooltip tooltip={tooltip} />
                  </label>
                  {isBool ? (
                    <BaseSwitch
                      id={id}
                      checked={!!val}
                      onChange={handleSwitch(settingName)}
                    />
                  ) : (
                    <BaseRoundedInput
                      id={id}
                      className="w-full min-w-0"
                      value={
                        (typeof val === 'boolean'
                          ? String(val)
                          : val ?? '') as string | number | undefined
                      }
                      onChange={handleInput(settingName)}
                    />
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

// --- Helpers ---
function getRelevantEditorReference(
  strategy: string,
  marketType: string,
  allTemplates: CustomStratEditors
) {
  if (allTemplates[strategy]) return strategy
  const normalized = strategy.toLowerCase()
  if (allTemplates[normalized]) return normalized
  if (marketType.includes('futures') && allTemplates['builder (futures)']) return 'builder (futures)'
  if (allTemplates['builder']) return 'builder'
  return Object.keys(allTemplates)[0] || ''
}

export default TableDetailPanelOfPair
